/* eslint-disable react/react-in-jsx-scope */
import { MAX_YEAR, MIN_YEAR, YearPicker } from 'components/YearPicker'
import {
  OptionalDateRangeCheckData,
  optionalDateRangeCheckDataToOptionalDateRange,
} from 'entities-next/scoutingCampaignCriteria'
import _ from 'lodash'
import { makeStyles } from 'tss-react/mui'
import { FieldProps, getIn } from 'utils-next/forms'
import { ExtendableStyles } from 'utils-next/types'

import { Theme } from '@mui/material'

export function FoundedAtPicker({ form: { errors, setFieldValue }, field: { name, value } }: FoundedAtPickerProps) {
  const { classes } = useStyles()

  const internalValue = value ?? {}

  return (
    <div className={classes.doubleFieldWrapper}>
      <YearPicker
        placeholder={'The minimum founding date'}
        helperText={
          getIn(errors, 'organizationFoundedAt.minimumYear') ??
          'You will receive companies founded after the selected year, including the selected one'
        }
        value={parseOptionalDate(internalValue.minimumYear ?? undefined)}
        minDate={parseOptionalDate(MIN_YEAR)}
        maxDate={parseOptionalDate(internalValue.maximumYear ?? MAX_YEAR)}
        error={getIn(errors, 'organizationFoundedAt.minimumYear') ?? false}
        onChange={(minimumYearValue: unknown) => {
          let minimumYear: number | undefined
          if (minimumYearValue instanceof Date) {
            minimumYear = minimumYearValue.getUTCFullYear()
          }

          onDateRangeChange({
            onChange: (dateRange) => {
              let parsedRange: undefined | OptionalDateRangeCheckData = dateRange
              if (!dateRange.minimumYear && !dateRange.maximumYear) {
                parsedRange = undefined
              }
              setFieldValue(name, parsedRange, true)
            },
            previousDateRange: internalValue,
            dateRangeChange: {
              minimumYear,
            },
          })
        }}
      />
      <YearPicker
        placeholder={'The maximum founding date'}
        helperText={
          getIn(errors, 'organizationFoundedAt.maximumYear') ??
          'You will receive companies founded before the selected year, including the selected one'
        }
        value={parseOptionalDate(internalValue?.maximumYear ?? undefined)}
        minDate={parseOptionalDate(internalValue?.minimumYear ?? MIN_YEAR)}
        maxDate={parseOptionalDate(MAX_YEAR)}
        error={getIn(errors, 'organizationFoundedAt.maximumYear') ?? false}
        onChange={(maximumYearValue: unknown) => {
          let maximumYear: number | undefined

          if (maximumYearValue instanceof Date) {
            maximumYear = maximumYearValue.getUTCFullYear()
          }

          onDateRangeChange({
            onChange: (dateRange) => {
              let parsedRange: undefined | OptionalDateRangeCheckData = dateRange
              if (!dateRange.minimumYear && !dateRange.maximumYear) {
                parsedRange = undefined
              }
              setFieldValue(name, parsedRange, true)
            },
            previousDateRange: internalValue,
            dateRangeChange: {
              maximumYear: maximumYear,
            },
          })
        }}
      />
    </div>
  )
}

function onDateRangeChange({
  previousDateRange,
  dateRangeChange,
  onChange,
}: {
  previousDateRange: OptionalDateRangeCheckData
  dateRangeChange: Partial<OptionalDateRangeCheckData>
  onChange: (dateRange: OptionalDateRangeCheckData) => void
}) {
  const newDateRangeCheck = _.cloneDeep({ ...previousDateRange, ...dateRangeChange })

  const { minimumYear, maximumYear } = optionalDateRangeCheckDataToOptionalDateRange(newDateRangeCheck)

  if (minimumYear && maximumYear && maximumYear < minimumYear) {
    newDateRangeCheck.maximumYear = newDateRangeCheck.minimumYear
  }

  onChange(newDateRangeCheck)
}

function parseOptionalDate(date: number | null): Date | null {
  if (date !== null && date !== undefined) {
    return new Date(Date.UTC(date, 1))
  }

  return null
}

export type FoundedAtPickerProps = ExtendableStyles & {
  className?: string
  foundedAtCriteria: YearRangeType
} & FieldProps

const useStyles = makeStyles()(({ spacing }: Theme) => ({
  doubleFieldWrapper: {
    display: 'grid',
    gridGap: spacing(6),
    gridTemplateColumns: '1fr 1fr',
    width: '100%',
  },
  picker: {
    width: '100%',
  },
  label: {
    position: 'relative',
  },
}))

export type YearRangeType = {
  minimumYear: number | null
  maximumYear: number | null
}
